import type { NutritionPlan, NutritionPreset, NutritionPriority, Profile } from './types';

export type BudgetMode = 'recommended' | 'custom';

export function formatDecimalInput(value: number | null | undefined, maximumFractionDigits = 3) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '';
  const scale = 10 ** maximumFractionDigits;
  return String(Math.round(Number(value) * scale) / scale);
}

export function formatPoints(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return Number(value).toFixed(1);
}

export function fuelPoints(input: {
  calories?: number | null;
  totalFatG?: number | null;
  saturatedFatG?: number | null;
  addedSugarG?: number | null;
  fiberG?: number | null;
  proteinG?: number | null;
}): number | null {
  const { calories, totalFatG, saturatedFatG, addedSugarG, fiberG, proteinG } = input;
  if ([calories, saturatedFatG, proteinG].some((value) => value === null || value === undefined)) return null;
  const unsaturatedFatG = totalFatG == null ? 0 : Math.max(0, totalFatG - saturatedFatG!);
  const raw = 0.0305 * calories!
    + 0.275 * saturatedFatG!
    + 0.12 * (addedSugarG ?? 0)
    - 0.098 * proteinG!
    - 0.25 * (fiberG ?? 0)
    - 0.04 * unsaturatedFatG;
  return Math.max(0, Math.round(raw));
}

export type NutritionPlanInput = Pick<NutritionPlan, 'priorities' | 'protein_target_g' | 'fiber_target_g' |
  'saturated_fat_limit_g' | 'added_sugar_limit_g' | 'sodium_limit_mg'>;

export function fuelPointsV2(input: {
  calories?: number | null; totalFatG?: number | null; saturatedFatG?: number | null;
  addedSugarG?: number | null; fiberG?: number | null; proteinG?: number | null; sodiumMg?: number | null;
}, plan: NutritionPlanInput): number | null {
  const missingCore = input.calories == null || input.saturatedFatG == null || input.proteinG == null;
  const missingFocus = (plan.priorities.includes('fiber') && input.fiberG == null)
    || (plan.priorities.includes('added_sugar') && input.addedSugarG == null)
    || (plan.priorities.includes('sodium') && input.sodiumMg == null);
  if (missingCore || missingFocus) return null;
  const unsaturatedFatG = input.totalFatG == null ? 0 : Math.max(0, input.totalFatG - input.saturatedFatG!);
  let raw = 0.0305 * input.calories! + 0.275 * input.saturatedFatG!
    + 0.12 * (input.addedSugarG ?? 0) - 0.098 * input.proteinG!
    - 0.25 * (input.fiberG ?? 0) - 0.04 * unsaturatedFatG;
  if (plan.priorities.includes('protein')) raw -= 5 * input.proteinG! / plan.protein_target_g;
  if (plan.priorities.includes('fiber')) raw -= 5 * input.fiberG! / plan.fiber_target_g;
  if (plan.priorities.includes('saturated_fat')) raw += 5 * input.saturatedFatG! / plan.saturated_fat_limit_g;
  if (plan.priorities.includes('added_sugar')) raw += 5 * input.addedSugarG! / plan.added_sugar_limit_g;
  if (plan.priorities.includes('sodium')) raw += 5 * input.sodiumMg! / plan.sodium_limit_mg;
  return Math.max(0, Math.round(raw));
}

export function fuelPointsV3(input: {
  calories?: number | null; totalFatG?: number | null; saturatedFatG?: number | null;
  addedSugarG?: number | null; fiberG?: number | null; proteinG?: number | null; sodiumMg?: number | null;
}, plan: NutritionPlanInput): number | null {
  const missingCore = input.calories == null || input.saturatedFatG == null || input.proteinG == null;
  const missingFocus = (plan.priorities.includes('fiber') && input.fiberG == null)
    || (plan.priorities.includes('added_sugar') && input.addedSugarG == null)
    || (plan.priorities.includes('sodium') && input.sodiumMg == null);
  if (missingCore || missingFocus) return null;
  const unsaturatedFatG = input.totalFatG == null ? 0 : Math.max(0, input.totalFatG - input.saturatedFatG!);
  let raw = 0.0305 * input.calories! + 0.275 * input.saturatedFatG!
    + 0.12 * (input.addedSugarG ?? 0) - 0.098 * input.proteinG!
    - 0.25 * (input.fiberG ?? 0) - 0.04 * unsaturatedFatG;
  if (plan.priorities.includes('protein')) raw -= 5 * input.proteinG! / plan.protein_target_g;
  if (plan.priorities.includes('fiber')) raw -= 5 * input.fiberG! / plan.fiber_target_g;
  if (plan.priorities.includes('saturated_fat')) raw += 5 * input.saturatedFatG! / plan.saturated_fat_limit_g;
  if (plan.priorities.includes('added_sugar')) raw += 5 * input.addedSugarG! / plan.added_sugar_limit_g;
  if (plan.priorities.includes('sodium')) raw += 5 * input.sodiumMg! / plan.sodium_limit_mg;
  return Math.round(Math.max(0, raw) * 10) / 10;
}

export function suggestedTargets(weightKg: number | null, fiberReference = 25) {
  return {
    proteinG: weightKg === null ? 0 : Math.round(Math.max(0, weightKg) * 10) / 10,
    fiberG: Math.max(0, fiberReference),
    waterMl: 1892.71,
  };
}

export function suggestedBudget(input: {
  weightKg: number | null;
  heightCm: number | null;
  ageYears: number | null;
  sex: 'female' | 'male' | 'unspecified';
  baselineActivity: 'mostly_seated' | 'some_movement' | 'active_day';
  proteinG: number;
  fiberG: number;
  priorities?: NutritionPriority[];
  saturatedFatLimitG?: number;
  addedSugarLimitG?: number;
  sodiumLimitMg?: number;
}) {
  const { weightKg, heightCm, ageYears, sex, baselineActivity, proteinG, fiberG, priorities = [] } = input;
  if (!weightKg || !heightCm || !ageYears) return null;
  const sexConstant = sex === 'male' ? 5 : sex === 'female' ? -161 : -78;
  const activityFactor = { mostly_seated: 1.2, some_movement: 1.375, active_day: 1.55 }[baselineActivity];
  const restingCalories = 10 * weightKg + 6.25 * heightCm - 5 * ageYears + sexConstant;
  const maintenanceCalories = restingCalories * activityFactor;
  const minimumCalories = sex === 'male' ? 1500 : sex === 'female' ? 1200 : 1350;
  const targetCalories = Math.round(Math.max(minimumCalories, maintenanceCalories * 0.8, maintenanceCalories - 500));
  const saturatedFatG = input.saturatedFatLimitG ?? targetCalories * 0.1 / 9;
  const addedSugarG = input.addedSugarLimitG ?? targetCalories * 0.05 / 4;
  const unsaturatedFatG = targetCalories * 0.15 / 9;
  let rawPoints = 0.0305 * targetCalories + 0.275 * saturatedFatG + 0.12 * addedSugarG
    - 0.098 * proteinG - 0.25 * fiberG - 0.04 * unsaturatedFatG;
  if (priorities.includes('protein')) rawPoints -= 5;
  if (priorities.includes('fiber')) rawPoints -= 5;
  if (priorities.includes('saturated_fat')) rawPoints += 5;
  if (priorities.includes('added_sugar')) rawPoints += 5;
  if (priorities.includes('sodium')) rawPoints += 5 * (input.sodiumLimitMg ?? 2300) / (input.sodiumLimitMg ?? 2300);
  const dailyPoints = Math.max(15, Math.round(rawPoints));
  return { dailyPoints, weeklyPoints: Math.max(14, Math.round(dailyPoints * 0.6)), targetCalories };
}

export const presetPriorities: Record<NutritionPreset, NutritionPriority[]> = {
  balanced: [], saturated_fat_fiber: ['saturated_fat', 'fiber'], lower_sodium: ['sodium'],
  protein_through_day: ['protein'], custom: [],
};

export function nutritionPlanDefaults(profile: Profile, preset: NutritionPreset) {
  const weightKg = profile.current_weight_kg == null ? null : Number(profile.current_weight_kg);
  const proteinG = Number(profile.active_nutrition_plan?.protein_target_g ?? profile.current_targets?.protein_g ?? suggestedTargets(weightKg).proteinG);
  const fiberG = Number(profile.active_nutrition_plan?.fiber_target_g ?? profile.current_targets?.fiber_g ?? 28);
  const base = suggestedBudget({
    weightKg, heightCm: profile.height_cm == null ? null : Number(profile.height_cm),
    ageYears: profile.age_years == null ? null : Number(profile.age_years),
    sex: profile.sex_for_energy ?? 'unspecified', baselineActivity: profile.baseline_activity ?? 'mostly_seated',
    proteinG, fiberG,
  });
  const targetCalories = base?.targetCalories ?? 2000;
  const priorities = [...presetPriorities[preset]];
  const saturatedFatLimitG = targetCalories * (preset === 'saturated_fat_fiber' ? 0.06 : 0.1) / 9;
  const addedSugarLimitG = targetCalories * 0.05 / 4;
  const sodiumLimitMg = 2300;
  const budget = suggestedBudget({
    weightKg, heightCm: profile.height_cm == null ? null : Number(profile.height_cm),
    ageYears: profile.age_years == null ? null : Number(profile.age_years),
    sex: profile.sex_for_energy ?? 'unspecified', baselineActivity: profile.baseline_activity ?? 'mostly_seated',
    proteinG, fiberG, priorities, saturatedFatLimitG, addedSugarLimitG, sodiumLimitMg,
  });
  return {
    priorities, proteinTargetG: proteinG, fiberTargetG: fiberG, waterTargetMl: Number(profile.current_targets?.water_ml ?? 1892.71),
    saturatedFatLimitG: Math.round(saturatedFatLimitG * 10) / 10,
    addedSugarLimitG: Math.round(addedSugarLimitG * 10) / 10, sodiumLimitMg,
    proteinPerMealG: priorities.includes('protein') ? Math.round(proteinG / 3) : null,
    dailyPoints: budget?.dailyPoints ?? Number(profile.daily_points_budget ?? 0),
    weeklyPoints: budget?.weeklyPoints ?? Number(profile.weekly_points_budget ?? 0),
    targetCalories,
  };
}

export function suggestedBudgetForProfile(profile: Profile) {
  const weightKg = profile.current_weight_kg == null ? null : Number(profile.current_weight_kg);
  const targets = suggestedTargets(weightKg, Number(profile.current_targets?.fiber_g ?? 25));
  return suggestedBudget({
    weightKg,
    heightCm: profile.height_cm == null ? null : Number(profile.height_cm),
    ageYears: profile.age_years == null ? null : Number(profile.age_years),
    sex: profile.sex_for_energy ?? 'unspecified',
    baselineActivity: profile.baseline_activity ?? 'mostly_seated',
    proteinG: Number(profile.current_targets?.protein_g ?? targets.proteinG),
    fiberG: Number(profile.current_targets?.fiber_g ?? targets.fiberG),
  });
}

export function initialBudgetMode(profile: Profile): BudgetMode {
  if (profile.points_budget_source === 'family_suggestion_v1') return 'recommended';
  const missingBudget = profile.daily_points_budget == null || profile.weekly_points_budget == null;
  return missingBudget && suggestedBudgetForProfile(profile) ? 'recommended' : 'custom';
}
